import Dog from '../dto/Dog';
import DogMemberRelation from '../dto/DogMemberRelation';
import DogUsabilityPass from '../dto/DogUsabilityPass';
import Error from '../model/Error';
import ExamResult from '../dto/club/ExamResult.js';
import axios from 'axios';

export default class DogRepository {

    static findAll(clubId, breed, breedSpecialization, withoutRelation, search, sort, limit) {
        return new Promise((resolve, reject) => {
            let params = {
                breed,
                breedSpecialization,
                withoutRelation,
                sort,
                limit
            };
            if (search !== '') {
                params.search = search;
            }
            axios
                .get(`/club/clubs/${clubId}/dogs`, { params })
                .then(response => resolve(response.data.data.map(data => Dog.fromData(data))))
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static create(clubId, dog) {
        return new Promise((resolve, reject) => {
            axios
                .post(`/club/clubs/${clubId}/dogs`, dog.serialize())
                .then(response => resolve(response.headers['location'].split('/').at(-2)))
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static update(dog) {
        return new Promise((resolve, reject) => {
            axios
                .put(`/club/dogs/${dog.id}`, dog.serialize())
                .then(response => resolve(response.headers['location']))
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static find(dogId) {
        return new Promise((resolve, reject) => {
            axios
                .get(`/club/dogs/${dogId}`)
                .then(response => resolve(Dog.fromData(response.data.data)))
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static remove(dogId) {
        return new Promise((resolve, reject) => {
            axios
                .delete(`/club/dogs/${dogId}`)
                .then(() => resolve())
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static uploadAvatar(dog, avatar, progressCallback) {
        const config = {
            onUploadProgress: function(progressEvent) {
                if (progressCallback) {
                    const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    progressCallback(percentCompleted);
                }
            },
            headers: { 'Content-Type': 'application/octet-stream' }
        };

        return new Promise((resolve, reject) => {
            axios
                .put(`/club/dogs/${dog.id}/avatar`, avatar, config)
                .then(() => resolve())
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static removeAvatar(dog) {
        return new Promise((resolve, reject) => {
            axios
                .delete(`/club/dogs/${dog.id}/avatar`)
                .then(() => resolve())
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static avatarUrl(dogId, fileName, size) {
        const fileSize = size || 'avatar';
        return `/api/club/dogs/${dogId}/avatar/${fileName}?size=${fileSize}`;
    }

    static findMemberRelations(dogId) {
        return new Promise((resolve, reject) => {
            axios
                .get(`/club/dogs/${dogId}/memberRelations`)
                .then(response => resolve(response.data.data.map(data => DogMemberRelation.fromData(data))))
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static addMemberRelation(clubId, dogId, relation) {
        let data = {
            dogId: dogId,
            memberId: relation.member.id,
            association: relation.association.value
        };

        return new Promise((resolve, reject) => {
            axios
                .post(`/club/clubs/${clubId}/dogMemberRelations`, data)
                .then(() => resolve())
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static removeMemberRelation(dogId, relationId) {
        return new Promise((resolve, reject) => {
            axios
                .delete(`/club/dogs/${dogId}/memberRelations/${relationId}`)
                .then(() => resolve())
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static createDogExam(dogId, examId, examOfferingId = null, examDate = null) {
        return new Promise((resolve, reject) => {
            axios
                .post(`/club/dogs/${dogId}/exam-results`, {
                    examId: examId,
                    examDate: examDate,
                    examOfferingId: examOfferingId,
                })
                .then((response) => resolve(response.headers['location'].split('/').at(-2)))
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static findAllExamResults(dogId, params) {
        return new Promise((resolve, reject) => {
            axios
                .get(`/club/dogs/${dogId}/exam-results`, { params })
                .then((response) => resolve(response.data.data.map((exam) => ExamResult.fromData(exam))))
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static findAllUsabilityPasses(dogId) {
        return new Promise((resolve, reject) => {
            axios
                .get(`/club/dogs/${dogId}/usability-passes`, {})
                .then((response) => resolve(response.data.data.map((exam) => DogUsabilityPass.fromData(exam))))
                .catch(error => reject(error));
        });
    }

    static findExamResult(dogId, examId) {
        return new Promise((resolve, reject) => {
            axios
                .get(`/club/dogs/${dogId}/exam-results/${examId}`)
                .then((response) => resolve(ExamResult.fromData(response.data.data)))
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static updateExamResult(dogId, result) {
        return new Promise((resolve, reject) => {
            axios
                .put(`/club/dogs/${dogId}/exam-results/${result.id}`, result.serialize())
                .then((response) => resolve(ExamResult.fromData(response.data.data)))
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

    static deleteExamResult(dogId, result) {
        return new Promise((resolve, reject) => {
            axios
                .delete(`/club/dogs/${dogId}/exam-results/${result.id}`)
                .then(() => resolve())
                .catch(error => reject(Error.fromResponse(error.response)));
        });
    }

}
